import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import MustChangePasswordModal from "@/components/auth/MustChangePasswordModal";
import CookieBanner from "@/components/CookieBanner";
import PushPermissionPrompt from "@/components/PushPermissionPrompt";
import PushNavListener from "@/components/PushNavListener";
import AppUpdateBanner from "@/components/AppUpdateBanner";
import "@/App.css";

// Code-splitting: le pagine pesanti (dashboard) e le sotto-pagine pubbliche
// vengono caricate on-demand → bundle iniziale ~70% più leggero, cruciale su mobile.
const Register = lazy(() => import("@/pages/Register"));
const ClientDashboard = lazy(() => import("@/pages/ClientDashboard"));
const InstructorDashboard = lazy(() => import("@/pages/InstructorDashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const BlogList = lazy(() => import("@/pages/BlogList"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const FranchisingPage = lazy(() => import("@/pages/FranchisingPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const GravidanzaPage = lazy(() => import("@/pages/GravidanzaPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const GiftCardPage = lazy(() => import("@/pages/GiftCardPage"));
const GiftCardViewPage = lazy(() => import("@/pages/GiftCardViewPage"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const MaintenancePage = lazy(() => import("@/pages/MaintenancePage"));
const LegalDocumentPage = lazy(() => import("@/pages/LegalDocumentPage"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
    Caricamento...
  </div>
);

const ADMIN_ROLES = ["admin", "super_admin", "sede_admin"];
const STAFF_ROLES = ["admin", "super_admin", "sede_admin", "instructor"];

function RoleGate() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const q = location.search || "";
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Caricamento...</div>;
  if (!user) return <Navigate to={`/login${q}`} replace />;
  if (ADMIN_ROLES.includes(user.role)) return <Navigate to={`/admin${q}`} replace />;
  if (user.role === "instructor") return <Navigate to={`/insegnante${q}`} replace />;
  return <Navigate to={`/area-cliente${q}`} replace />;
}

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Caricamento...</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`} replace />;
  const roleOk = !role || user.role === role || (role === "admin" && ADMIN_ROLES.includes(user.role));
  if (!roleOk) return <Navigate to="/dashboard" replace />;
  return children;
}

/**
 * MaintenanceGate — quando il flag di manutenzione è attivo, mostra la
 * pagina di manutenzione a chiunque visiti il dominio, tranne allo staff
 * autenticato (super_admin, sede_admin, admin, instructor).
 *
 * Punto di ingresso privilegiato per lo staff: `/login` resta sempre
 * accessibile (senza link visibili in pagina di manutenzione). Anche
 * `/admin`, `/insegnante`, `/auth/callback` e le route di pagamento
 * restano raggiungibili per completare i flussi staff.
 */
function MaintenanceGate({ children }) {
  const { user, loading, maintenanceMode } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Caricamento...</div>;
  }

  if (!maintenanceMode) return children;

  const isStaff = user && STAFF_ROLES.includes(user.role);
  if (isStaff) return children;

  // Rotte sempre raggiungibili anche in manutenzione (ingresso staff + callback)
  const path = location.pathname || "/";
  const allowlist = [
    "/login",
    "/auth/callback",
    "/payment/success",
    "/payment/cancel",
  ];
  const isAllowed = allowlist.some((p) => path === p || path.startsWith(p + "/"));
  if (isAllowed) return children;

  return (
    <Suspense fallback={<RouteFallback />}>
      <MaintenancePage />
    </Suspense>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <MaintenanceGate>
      <AppUpdateBanner />
      <MustChangePasswordModal />
      <CookieBanner />
      <PushNavListener />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/eventi" element={<EventsPage />} />
          <Route path="/franchising" element={<FranchisingPage />} />
          <Route path="/pacchetti" element={<PricingPage />} />
          <Route path="/legal/termini" element={<LegalDocumentPage kind="terms" />} />
          <Route path="/legal/privacy" element={<LegalDocumentPage kind="privacy" />} />
          <Route path="/legal/gdpr" element={<LegalDocumentPage kind="gdpr" />} />
          <Route path="/legal/cookie" element={<LegalDocumentPage kind="cookie" />} />
          <Route path="/legal/regolamento/:sedeId" element={<LegalDocumentPage kind="regulation" />} />
          <Route path="/gravidanza" element={<GravidanzaPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/registrati" element={<Register />} />
          <Route path="/password-dimenticata" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/gift-card" element={<Protected role="client"><GiftCardPage /></Protected>} />
          <Route path="/gift-card/grazie" element={<GiftCardViewPage />} />
          <Route path="/gift-card/view/:cardId" element={<GiftCardViewPage />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/dashboard" element={<RoleGate />} />
          <Route path="/area-cliente" element={<Protected role="client"><ClientDashboard /></Protected>} />
          <Route path="/insegnante" element={<Protected role="instructor"><InstructorDashboard /></Protected>} />
          <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </MaintenanceGate>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            richColors
            closeButton
            expand
            duration={4500}
            toastOptions={{
              className: "font-sans",
              style: {
                borderRadius: "16px",
                fontFamily: "'Manrope', system-ui, sans-serif",
              },
            }}
          />
          <AppRoutes />
          <PushPermissionPrompt />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
